/**
 * /auto-ecoles/:dept/:villeSlug — Page ville : liste complète + carte + FAQ + JSON-LD.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Award, Globe, ChevronRight, ArrowLeft, Home, ShieldCheck, HelpCircle, Info, ExternalLink } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { API, SITE_NAME, useSeoMeta, formatDateFR, permisLabel } from './_shared';

const roseIcon = new L.DivIcon({
  className: 'c2r-marker',
  html: '<div style="width:22px;height:22px;background:#e11d48;border:3px solid white;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.5);"></div>',
  iconSize: [22, 22], iconAnchor: [11, 11],
});

function MiniMap({ items, ville }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;
    // Cleanup safe (StrictMode double-mount protection)
    if (mapRef.current) {
      try { mapRef.current.off(); mapRef.current.remove(); } catch (_) {}
      mapRef.current = null;
    }
    const points = items.filter(s => s.lat && s.lng);
    if (points.length === 0) return;
    const map = L.map(containerRef.current, {
      center: [points[0].lat, points[0].lng],
      zoom: 13,
      scrollWheelZoom: false,
      preferCanvas: true,
      zoomAnimation: false, // évite _leaflet_pos race
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OpenStreetMap', maxZoom: 19 }).addTo(map);
    points.forEach(s => {
      L.marker([s.lat, s.lng], { icon: roseIcon })
        .bindPopup(`<div style="font-weight:800;font-size:12px">${s.nom}</div><div style="font-size:11px;color:#666">${s.adresse}</div>`)
        .addTo(map);
    });
    if (points.length > 1) {
      const bounds = L.latLngBounds(points.map(s => [s.lat, s.lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14, animate: false });
    }
    mapRef.current = map;
    return () => {
      cancelled = true;
      if (mapRef.current) {
        try { mapRef.current.off(); mapRef.current.remove(); } catch (_) {}
        mapRef.current = null;
      }
    };
  }, [items]);
  return <div ref={containerRef} className="rounded-2xl overflow-hidden border border-white/10" style={{ height: 340, background: '#0d0d0e' }} data-testid="ville-mini-map" />;
}

function SchoolCard({ school, dept, villeSlug }) {
  const gmaps = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${school.nom} ${school.adresse} ${school.ville}`)}`;
  const isVerified = school.claim_status === 'verifiee' || school.claim_status === 'premium';
  const isEditorial = school.source_type === 'editorial';
  return (
    <article className={`rounded-2xl bg-[#141416] border transition p-4 sm:p-5 flex flex-col gap-3 ${school.featured_rank != null ? 'border-amber-400/50 ring-1 ring-amber-400/25 bg-[#1a1712]' : 'border-white/10 hover:border-rose-500/30 hover:bg-[#181819]'}`} data-testid={`ville-card-${school.id}`}>
      {school.featured_rank != null && (
        <span className="inline-flex items-center gap-1 self-start px-2 py-0.5 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-300 text-[9px] font-black uppercase tracking-widest" data-testid={`ville-featured-badge-${school.id}`}>
          ★ {school.featured_partner || 'Mise en avant'}
        </span>
      )}
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-white font-black text-[15px] sm:text-[16px] leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {school.nom}
            {isVerified && <span className="ml-2 inline-flex items-center align-middle px-1.5 py-0.5 rounded bg-blue-500/15 border border-blue-500/30 text-blue-300 text-[9px] font-black uppercase tracking-widest"><ShieldCheck className="w-2.5 h-2.5 mr-0.5" />Vérifiée</span>}
          </h3>
          <p className="text-white/50 text-[11.5px] mt-1 flex items-start gap-1.5">
            <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
            <span>{school.adresse}<br />{school.code_postal} {school.ville}</span>
          </p>
          {school.custom_phone && (
            <p className="text-white/50 text-[11.5px] mt-1"><span className="font-mono">☎ {school.custom_phone}</span></p>
          )}
          {school.custom_description && <p className="text-white/70 text-[12.5px] mt-2 line-clamp-3">{school.custom_description}</p>}
          {isEditorial && (
            <p className="mt-2 inline-flex items-start gap-1.5 text-[10px] text-amber-300/80 bg-amber-500/10 border border-amber-500/20 rounded-md px-2 py-1">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              <span>Données éditoriales à confirmer par la Sécurité Routière · <em>vérifiées le {school.source_date || 'récemment'}</em>.</span>
            </p>
          )}
        </div>
        {school.label_qualite && (
          <span className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[9px] font-black uppercase tracking-widest"><Award className="w-2.5 h-2.5" /> Qualité</span>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-1.5">
        {(school.permis || []).map(p => (
          <span key={p} className="px-2 py-0.5 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-200 text-[10px] font-black uppercase tracking-widest">{p}</span>
        ))}
        {typeof school.taux_reussite_moto === 'number' && (
          <span className="ml-auto text-white/60 text-[11px] font-mono">🏁 {Math.round(school.taux_reussite_moto * 100)}% réussite</span>
        )}
      </div>

      <div className="flex flex-wrap gap-2 pt-1 mt-auto">
        <a href={gmaps} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border border-white/15 bg-white/5 text-white hover:bg-white/10 text-[11px] font-semibold transition" data-testid={`ville-card-map-${school.id}`}>
          <MapPin className="w-3.5 h-3.5" /> Voir sur la carte
        </a>
        {(school.custom_website || school.site_internet) && (
          <a href={(school.custom_website || school.site_internet).startsWith('http') ? (school.custom_website || school.site_internet) : `https://${school.custom_website || school.site_internet}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-[11px] font-black uppercase tracking-widest transition" data-testid={`ville-card-site-${school.id}`}>
            <Globe className="w-3.5 h-3.5" /> Site
          </a>
        )}
        <Link to={`/auto-ecoles/${dept}/${villeSlug}/${school.id}/gerer`} className="ml-auto inline-flex items-center gap-1 h-9 px-2.5 rounded-xl border border-white/10 bg-white/[0.03] text-white/60 hover:text-white hover:bg-white/10 text-[10.5px] transition" data-testid={`ville-card-gerer-${school.id}`}>
          <Info className="w-3 h-3" /> Modifier / Retirer
        </Link>
      </div>
    </article>
  );
}

export default function AutoEcolesVille() {
  const { dept, villeSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/annuaire/ville/${dept}/${villeSlug}`)
      .then(r => { if (!r.ok) throw new Error('not_found'); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [dept, villeSlug]);

  const villeTitle = data ? data.ville : villeSlug;
  const total = data?.total || 0;

  useSeoMeta({
    title: data ? `${total} auto-écoles à ${villeTitle} (${dept}) · Permis B, AM, B96, BE · ${SITE_NAME}` : `${villeSlug} · ${SITE_NAME}`,
    description: data ? `Trouve ton auto-école à ${villeTitle} (${dept}) parmi ${total} établissements agréés. Permis B, AM, B96, BE · Sources officielles Sécurité Routière · Mise à jour ${formatDateFR(data.updated_at)}.` : '',
    canonicalPath: `/auto-ecoles/${dept}/${villeSlug}`,
    jsonLd: data ? {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `Auto-écoles à ${villeTitle}`,
      numberOfItems: total,
      itemListElement: (data.items || []).slice(0, 30).map((s, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        item: {
          '@type': 'LocalBusiness',
          name: s.nom,
          address: {
            '@type': 'PostalAddress',
            streetAddress: s.adresse,
            postalCode: s.code_postal,
            addressLocality: s.ville,
            addressCountry: 'FR',
          },
          ...(s.lat && s.lng ? { geo: { '@type': 'GeoCoordinates', latitude: s.lat, longitude: s.lng } } : {}),
          ...(s.site_internet ? { url: s.site_internet.startsWith('http') ? s.site_internet : `https://${s.site_internet}` } : {}),
        },
      })),
    } : null,
  });

  if (loading) return <div className="min-h-screen bg-[#0a0a0b] text-white/50 p-8 text-sm">Chargement…</div>;
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] text-white p-8">
        <p className="text-white/60">Aucune auto-école recensée à cette adresse.</p>
        <Link to={`/auto-ecoles/${dept}`} className="mt-4 inline-flex items-center gap-2 text-rose-300 underline">← Retour au département</Link>
      </div>
    );
  }

  const permisDispo = Object.entries(data.permis_counts || {}).filter(([, n]) => n > 0);

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white pb-16" data-testid="auto-ecoles-ville">
      {/* Breadcrumb */}
      <div className="border-b border-white/10 bg-black/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 text-[12px] text-white/50 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-white"><Home className="w-3.5 h-3.5 inline" /></Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/auto-ecoles" className="hover:text-white">Annuaire</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={`/auto-ecoles/${dept}`} className="hover:text-white">{data.dept_name}</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-white/80">{villeTitle}</span>
        </div>
      </div>

      {/* Hero */}
      <header className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <Link to={`/auto-ecoles/${dept}`} className="inline-flex items-center gap-1 text-white/50 hover:text-white text-[12px] mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Toutes les villes du {dept}
        </Link>
        <h1 className="text-3xl sm:text-4xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Auto-écoles à {villeTitle}
        </h1>
        <p className="text-white/60 mt-2 text-sm sm:text-base">
          <strong className="text-white">{total} établissement{total > 1 ? 's' : ''} agréé{total > 1 ? 's' : ''}</strong> à {villeTitle} · département {data.dept_name} ({dept}).
        </p>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {permisDispo.map(([p, n]) => (
            <span key={p} className="px-2.5 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-200 text-[10.5px] font-black uppercase tracking-widest">
              {permisLabel(p)} · {n}
            </span>
          ))}
        </div>
      </header>

      {/* Carte */}
      {data.items.some(s => s.lat && s.lng) && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-8">
          <MiniMap items={data.items} ville={villeTitle} />
        </section>
      )}

      {/* Cards */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {data.items.map(s => (
            <SchoolCard key={s.id} school={s} dept={dept} villeSlug={villeSlug} />
          ))}
        </div>
      </section>

      {/* Texte SEO */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 mt-12 space-y-4 text-white/70 text-[13.5px] leading-relaxed">
        <h2 className="text-white text-lg font-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Passer son permis de conduire à {villeTitle}
        </h2>
        <p>
          À <strong>{villeTitle}</strong>, <strong>{total} auto-école{total > 1 ? 's' : ''}</strong> {total > 1 ? 'sont agréées' : 'est agréée'} pour dispenser la formation aux permis B, AM, B96 et BE. Ces établissements figurent sur la liste officielle des écoles de conduite délivrée par la Sécurité Routière et publiée sur data.gouv.fr.
        </p>
        {data.label_qualite_count > 0 && (
          <p>
            <strong className="text-emerald-300"><Award className="inline w-3.5 h-3.5 mr-1" />{data.label_qualite_count} école{data.label_qualite_count > 1 ? 's' : ''}</strong> détient à ce jour le <em>Label Qualité des Formations aux Métiers de la Route</em>, gage d'engagement pédagogique et de sérieux dans l'apprentissage.
          </p>
        )}
        <p>
          Chaque fiche affiche les permis dispensés, l'adresse professionnelle et le site officiel. Aucune donnée personnelle du dirigeant n'est publiée.
        </p>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 mt-10">
        <h2 className="text-white text-lg font-black mb-4 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          <HelpCircle className="w-4 h-4 text-rose-400" /> Questions fréquentes
        </h2>
        <Accordion type="single" collapsible className="space-y-2">
          <AccordionItem value="q1" className="rounded-xl bg-[#141416] border border-white/10 px-4">
            <AccordionTrigger className="text-white text-[13.5px] font-bold text-left">Comment sont sélectionnées les auto-écoles de {villeTitle} ?</AccordionTrigger>
            <AccordionContent className="text-white/60 text-[13px]">Nous reprenons intégralement la liste officielle des écoles agréées publiée par la <strong>Sécurité Routière</strong> sur data.gouv.fr. Aucune école n'est ajoutée ni retirée manuellement, sauf demande de retrait exercée dans le cadre du droit d'opposition RGPD.</AccordionContent>
          </AccordionItem>
          <AccordionItem value="q2" className="rounded-xl bg-[#141416] border border-white/10 px-4">
            <AccordionTrigger className="text-white text-[13.5px] font-bold text-left">Que signifie le badge « Label Qualité » ?</AccordionTrigger>
            <AccordionContent className="text-white/60 text-[13px]">Le <em>Label Qualité des Formations aux Métiers de la Route</em> est décerné par l'État aux écoles répondant à un cahier des charges pédagogique et administratif exigeant (formation continue des moniteurs, transparence tarifaire, suivi des élèves).</AccordionContent>
          </AccordionItem>
          <AccordionItem value="q3" className="rounded-xl bg-[#141416] border border-white/10 px-4">
            <AccordionTrigger className="text-white text-[13.5px] font-bold text-left">Je suis gérant, comment modifier ma fiche ?</AccordionTrigger>
            <AccordionContent className="text-white/60 text-[13px]">Cliquez sur le bouton « Modifier / Retirer » de votre fiche : vous pourrez la <strong>revendiquer gratuitement</strong> (SIRET + email pro), demander une correction, ou exercer votre <strong>droit d'opposition RGPD</strong> pour retirer votre établissement de l'annuaire.</AccordionContent>
          </AccordionItem>
          <AccordionItem value="q4" className="rounded-xl bg-[#141416] border border-white/10 px-4">
            <AccordionTrigger className="text-white text-[13.5px] font-bold text-left">Quelle est la différence entre les permis B, AM, B96 et BE ?</AccordionTrigger>
            <AccordionContent className="text-white/60 text-[13px]">Le <strong>permis B</strong> (dès 17 ans, conduite dès 18 ans) autorise la conduite d'une voiture. Le <strong>permis AM</strong> (dès 14 ans) permet de conduire une voiturette (quadricycle léger). Le <strong>B96</strong> est une formation de 7 h pour tracter une remorque lourde (ensemble ≤ 4 250 kg), et le <strong>permis BE</strong> couvre les remorques de plus de 3 500 kg d'ensemble.</AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>

      {/* Source */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 mt-10 pt-6 border-t border-white/5 text-[11.5px] text-white/40 space-y-1">
        <p>
          <ShieldCheck className="inline w-3.5 h-3.5 mr-1 text-emerald-400" />
          <strong className="text-white/60">Source</strong> : Sécurité Routière · <a href="https://www.data.gouv.fr/datasets/liste-des-auto-ecoles-et-taux-de-reussite-au-permis-de-conduire/" target="_blank" rel="noopener noreferrer" className="underline hover:text-rose-300">data.gouv.fr</a>
          {data.updated_at && <> · <strong className="text-white/60">Dernière vérification</strong> : {formatDateFR(data.updated_at)}</>}.
        </p>
        <p>
          <Link to="/mentions-annuaire" className="underline hover:text-rose-300">Mentions légales &amp; RGPD</Link> · Aucune donnée personnelle du dirigeant n'est affichée.
        </p>
      </section>
    </div>
  );
}
